import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Bed, 
  Bath, 
  Wrench, 
  Sparkles, 
  MessageCircle, 
  Wifi, 
  Tv, 
  ThermometerSun,
  MapPin,
  Phone,
  CheckCircle,
  Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function RoomService() {
  const [searchParams] = useSearchParams();
  const room = searchParams.get('room') || searchParams.get('location') || 'Unknown';
  const roomNumber = room.replace('room', '').trim();
  
  const [loading, setLoading] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [customDetails, setCustomDetails] = useState('');
  const [showChat, setShowChat] = useState(false);

  const serviceOptions = [
    { id: 'towels', icon: Bath, label: 'Extra Towels', color: 'blue' },
    { id: 'pillows', icon: Bed, label: 'Extra Pillows', color: 'purple' },
    { id: 'blankets', icon: Bed, label: 'Extra Blankets', color: 'indigo' },
    { id: 'toiletries', icon: Sparkles, label: 'Toiletries', color: 'pink' },
    { id: 'housekeeping', icon: Sparkles, label: 'Housekeeping', color: 'green' },
    { id: 'maintenance', icon: Wrench, label: 'Maintenance', color: 'orange' },
  ];

  const submitRequest = async (requestType) => {
    setLoading(true);
    setSelectedRequest(requestType);
    
    try {
      const response = await axios.post(`${API}/api/room-service/request`, {
        room: roomNumber,
        request_type: requestType,
        details: customDetails || null
      });
      
      if (response.data.success) {
        setRequestSent(true);
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error('Request failed:', error);
      toast.error('Failed to send request. Please call the front desk.');
    }
    
    setLoading(false);
  };

  if (requestSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="bg-emerald-500/20 p-6 rounded-full inline-block mb-6">
            <CheckCircle className="w-16 h-16 text-emerald-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">Request Sent!</h1>
          <p className="text-gray-400 mb-6 max-w-md">
            Our team has been notified and someone will be at Room {roomNumber} shortly.
          </p>
          <button
            onClick={() => {
              setRequestSent(false);
              setSelectedRequest(null);
              setCustomDetails('');
            }}
            className="text-orange-400 hover:text-orange-300"
          >
            Make Another Request
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center py-6">
          <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-400">
            Room {roomNumber}
          </h1>
          <p className="text-gray-400 mt-1">How can we help you?</p>
        </div>

        {/* Quick Info Cards */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4">
            <Wifi className="w-5 h-5 text-blue-400 mb-2" />
            <p className="text-xs text-gray-500">Wi-Fi Network</p>
            <p className="text-white font-semibold">Hodlerinn</p>
            <p className="text-xs text-gray-400">Password: opendoor</p>
          </div>
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4">
            <Phone className="w-5 h-5 text-green-400 mb-2" />
            <p className="text-xs text-gray-500">Front Desk</p>
            <p className="text-white font-semibold">(918) 653-7801</p>
            <p className="text-xs text-gray-400">24/7 Available</p>
          </div>
        </div>

        {/* Service Request Grid */}
        <div className="bg-gray-800/30 border border-gray-700/50 rounded-2xl p-4 mb-6">
          <h2 className="text-white font-semibold mb-4">Request Service</h2>
          <div className="grid grid-cols-2 gap-3">
            {serviceOptions.map((option) => (
              <motion.button
                key={option.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => submitRequest(option.id)}
                disabled={loading}
                className={`bg-gray-800/50 border border-gray-700 rounded-xl p-4 text-left hover:border-orange-500/50 transition-all ${
                  loading && selectedRequest === option.id ? 'opacity-50' : ''
                }`}
              >
                <option.icon className={`w-6 h-6 text-${option.color}-400 mb-2`} />
                <p className="text-white font-medium text-sm">{option.label}</p>
                {loading && selectedRequest === option.id && (
                  <Loader2 className="w-4 h-4 animate-spin text-orange-400 mt-2" />
                )}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Other Request */}
        <div className="bg-gray-800/30 border border-gray-700/50 rounded-2xl p-4 mb-6">
          <h2 className="text-white font-semibold mb-3">Other Request</h2>
          <textarea
            value={customDetails}
            onChange={(e) => setCustomDetails(e.target.value)}
            placeholder="Describe what you need..."
            className="w-full bg-gray-900/50 border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 resize-none h-20"
          />
          <button
            onClick={() => submitRequest('other')}
            disabled={loading || !customDetails.trim()}
            className="w-full mt-3 bg-orange-500 text-black font-semibold py-3 rounded-lg hover:bg-orange-400 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading && selectedRequest === 'other' ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Sending...
              </span>
            ) : (
              'Send Request'
            )}
          </button>
        </div>

        {/* Chat with Bitsy */}
        <div className="bg-gray-800/30 border border-orange-500/30 rounded-2xl p-4 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-orange-500/20 p-2 rounded-full">
              <MessageCircle className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <h2 className="text-white font-semibold">Chat with Bitsy</h2>
              <p className="text-xs text-gray-400">Ask anything about your stay</p>
            </div>
          </div>
          <a
            href={`/#/book?source=room&location=room${roomNumber}`}
            className="block w-full bg-orange-500/20 border border-orange-500/30 text-orange-400 font-semibold py-3 rounded-lg text-center hover:bg-orange-500/30"
          >
            Open Chat
          </a>
        </div>

        {/* Local Recommendations */}
        <div className="bg-gray-800/30 border border-gray-700/50 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-5 h-5 text-emerald-400" />
            <h2 className="text-white font-semibold">Local Picks</h2>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-gray-400">
              <span>🍽️ Southern Belle Restaurant</span>
              <span className="text-gray-500">5 min</span>
            </div>
            <div className="flex justify-between text-gray-400">
              <span>🏛️ Heavener Runestone Park</span>
              <span className="text-gray-500">3 min</span>
            </div>
            <div className="flex justify-between text-gray-400">
              <span>🛒 Walmart (Poteau)</span>
              <span className="text-gray-500">15 min</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center py-6 text-gray-500 text-xs">
          <p>Check-out by 11:00 AM</p>
          <p className="mt-1">Thank you for staying at Hodler Inn!</p>
        </div>
      </div>
    </div>
  );
}
